import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, Headphones, Video } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { mediaUrl, announcementEvent, BANNER_TAP_KEY } from '../lib/programsApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SERIF = { fontFamily: "'Playfair Display', serif" };
const LS = 'rbr_dismissed_announcements';
const lsGet = () => { try { return JSON.parse(localStorage.getItem(LS) || '[]'); } catch { return []; } };

// Launch banner (Today + Programs). In normal page flow — never covers the player or navigation.
// Shown only during the scheduled banner phase; afterwards the Programs card carries a small ribbon instead.
export const LaunchBanner = ({ placement }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const seenRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/announcements/active?placement=${placement}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (!cancelled) setItem((d.items || []).find((a) => !lsGet().includes(a.id)) || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [placement, user?.id]);
  useEffect(() => {
    if (!item || seenRef.current === `${item.id}:${placement}`) return;
    seenRef.current = `${item.id}:${placement}`;
    announcementEvent(item.id, 'impression', placement);
  }, [item, placement]);
  if (!item) return null;
  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(LS, JSON.stringify([...lsGet(), item.id]));
    setItem(null);
    const token = localStorage.getItem('rbr_token');
    if (token) fetch(`${API_URL}/api/announcements/${item.id}/dismiss`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };
  const tap = () => {
    try { if (item.programSlug) localStorage.setItem(BANNER_TAP_KEY(item.programSlug), JSON.stringify({ aid: item.id, at: Date.now() })); } catch { /* ignore */ }
    announcementEvent(item.id, 'tap', placement);
    navigate(item.url);
  };
  const cover = mediaUrl(item.coverImageUrl, 900);
  return (
    <section className="px-5 mb-5 animate-fade-in-up" data-testid={`launch-banner-${placement}`}>
      <div className="relative overflow-hidden rounded-3xl bg-[#0f1e33] text-[#f9f4ea] shadow-xl ring-1 ring-white/10">
        {cover && <img src={cover} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-35" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1e33] via-[#0f1e33]/85 to-[#0f1e33]/30" />
        <button type="button" onClick={dismiss} aria-label="Dismiss announcement" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center transition-colors" data-testid="launch-banner-dismiss"><X className="w-4 h-4" /></button>
        <div className="relative p-5 sm:p-6 sm:pr-40">
          <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D6AD60]" data-testid="launch-banner-eyebrow">{item.eyebrow}</p>
          <h2 className="mt-1 text-xl sm:text-2xl leading-tight" style={SERIF} data-testid="launch-banner-title">{item.title}</h2>
          <p className="mt-2 text-sm text-[#e3d2a8]/90 leading-relaxed max-w-xl" data-testid="launch-banner-body">{item.body}</p>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-[#e3d2a8]/75"><span className="inline-flex items-center gap-1"><Headphones className="w-3.5 h-3.5" /> Audio</span><span className="inline-flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Video</span><span>· 7 sessions · ~5 min each</span></div>
          <button type="button" onClick={tap} className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#c9a866] active:scale-[0.98] transition-all" data-testid="launch-banner-cta">
            {item.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
